import { useCallback } from "react"
import { sanitize } from "sanitize-filename-ts"
import { useMutation } from "urql"

import { cleanS3FileName } from "@/components/upload/utils.tsx"
import type {
  CreateFileRecordInput,
  FileMetadata,
  FileUploadMetadata,
  GeneratePresignedUrlMutation,
} from "@/graphql/codegen/graphql"
import { CreateFileRecordDocument, GeneratePresignedUrlDocument } from "@/graphql/codegen/graphql"
import { useAuthStore } from "@/stores/authStore"

interface FileUploadParams {
  file: File
  type: string
  metadata: Omit<FileMetadata, "fileName" | "label" | "userId">
  label?: string
}

interface SignedUrlResult {
  signedUrl: string | null
  fileUploadMetadata: FileUploadMetadata | null
  bucketLocation: string | null
}

interface UseFileUploadReturn {
  getSignedUrl: (params: FileUploadParams) => Promise<SignedUrlResult>
  uploadToS3: (signedUrl: string, file: File) => Promise<boolean>
  createRecord: (input: CreateFileRecordInput) => Promise<boolean>
  uploadFile: (params: FileUploadParams) => Promise<boolean>
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [, getFileSignedURL] = useMutation<GeneratePresignedUrlMutation>(GeneratePresignedUrlDocument)
  const [, createFileRecord] = useMutation(CreateFileRecordDocument)
  const userId = useAuthStore.getState().user?.id

  const getSignedUrl = useCallback(
    async ({ file, type, metadata, label }: FileUploadParams): Promise<SignedUrlResult> => {
      if (!userId) {
        console.error("No user ID available")
        return { signedUrl: null, fileUploadMetadata: null, bucketLocation: null }
      }

      const fileName = cleanS3FileName(sanitize(file.name))
      const fileExtension = fileName.substring(fileName.lastIndexOf("."))
      const baseFileName = `${type}${Date.now()}`

      try {
        const response = await getFileSignedURL({
          input: {
            fileMetadata: {
              ...metadata,
              fileName: fileName || baseFileName + fileExtension,
              label: label ?? baseFileName + fileExtension,
              userId,
            },
          },
        })

        if (!response.data) {
          const requestId = response.operation?.key || "N/A"
          console.error(`No data received from signed URL generation - ref: ${requestId}`)
          return { signedUrl: null, fileUploadMetadata: null, bucketLocation: null }
        }

        const result = response.data.fileUpload.generatePresignedUrl
        if (result.__typename !== "GetPresignedUrlSuccess") {
          const requestId = response.operation?.key || "N/A"
          console.error(`Failed to generate signed URL - ref: ${requestId}`)
          return { signedUrl: null, fileUploadMetadata: null, bucketLocation: null }
        }

        return {
          signedUrl: result.presignedUrl || null,
          fileUploadMetadata: result.fileMetadata || null,
          bucketLocation: result.bucketLocation || null,
        }
      } catch (error) {
        console.error("Error generating signed URL:", error)
        return { signedUrl: null, fileUploadMetadata: null, bucketLocation: null }
      }
    },
    [getFileSignedURL, userId]
  )

  const uploadToS3 = useCallback(async (signedUrl: string, file: File): Promise<boolean> => {
    try {
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: new File([file], cleanS3FileName(sanitize(file.name)), { type: file.type }),
        headers: {
          "Content-Type": file.type,
        },
      })

      if (!response.ok) {
        console.error(`Upload failed - status: ${response.status}`)
        return false
      }

      return true
    } catch (error) {
      if (error instanceof Error) {
        console.error(`Network error during upload: ${error.message}`)
      } else {
        console.error("Network error during upload")
      }
      return false
    }
  }, [])

  const createRecord = useCallback(
    async (input: CreateFileRecordInput): Promise<boolean> => {
      try {
        const createRecordResponse = await createFileRecord({ input })

        if (!createRecordResponse.data?.fileUpload.createFileRecord) {
          console.error("Failed to create file record in database")
          return false
        }

        const result = createRecordResponse.data.fileUpload.createFileRecord
        if (result.__typename !== "CreateFileRecordSuccess") {
          console.error("Failed to create file record:", result.error?.message)
          return false
        }
        return true
      } catch (error) {
        console.error("Error creating file record in database:", error)
        return false
      }
    },
    [createFileRecord]
  )

  const uploadFile = useCallback(
    async (params: FileUploadParams): Promise<boolean> => {
      const { signedUrl, fileUploadMetadata, bucketLocation } = await getSignedUrl(params)

      if (!signedUrl || !fileUploadMetadata || !bucketLocation) {
        console.error("Failed to get signed URL or metadata")
        return false
      }

      const isUploadedToS3 = await uploadToS3(signedUrl, params.file)
      if (!isUploadedToS3) {
        console.error("Failed to upload file to S3")
        return false
      }

      return await createRecord({
        fileId: fileUploadMetadata.fileId!,
        referenceId: fileUploadMetadata.referenceId!,
        referenceTableName: fileUploadMetadata.referenceTableName!,
        fieldGroup: fileUploadMetadata.fieldGroup!,
        label: fileUploadMetadata.label!,
        originalName: fileUploadMetadata.fileName!,
        bucketLocation,
        fileType: fileUploadMetadata.fileType!,
      })
    },
    [getSignedUrl, uploadToS3, createRecord]
  )

  return {
    getSignedUrl,
    uploadToS3,
    createRecord,
    uploadFile,
  }
}
